import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '../../../../../../hooks';
import { useOfferFormContext } from '../context';
import { getProductsModalOffer } from '../services';
import { normalizeModalProductFromSearch } from '../utils/normalizeModalProductFromSearch';

type Params = {
  idsLojas: any[];
  blocosOfertas: { label: string; value: number }[];
  setOferta: (data: any) => void;
  id: string;
  ofertaId?: string | number;
  show: boolean;
  initialProducts?: ProdutoDetalhado[];
};

type ProdutoLista = {
  product_id: string;
  description: string;
  is_product_used?: boolean;
  checked?: boolean;
  value?: string | number;
};

export type ProdutoDetalhado = {
  product_id: string;
  product_uuid?: string;
  description: string;
  pmz?: number;
  retail_price?: number;
  retail_price_cpi?: number;
  avg_competitiveness_price?: number;
  offer_price_cpi?: number;
  margin?: number;
  margin_price_retail?: number;
  product_family_id?: string | number;
  store_id: string;
  cost: number;
  avg_price: number;
  category_level1: string;
  category_level2: string;
  category_level3: string;
  category_level4: string;
  client_id: number;
};

type OfferBlock = { label: string; value: number } | null;

export const useProdutosCampanha = ({ idsLojas, blocosOfertas, show, id, setOferta, initialProducts }: Params) => {
  const [searchProduct, setSearchProduct] = useState('');
  const [productsTableState, setProductsTableState] = useState<ProdutoDetalhado[]>(initialProducts || []);
  const [sortType, setSortType] = useState<'asc' | 'desc'>('desc');
  const [sortColumn, setSortColumn] = useState<string>('');
  const { offerForm, offerType: offerTypeSelected } = useOfferFormContext();
  const [blocoDeOferta, setBlocoDeOferta] = useState<OfferBlock>(null);

  const [isPasteModalOpen, setIsPasteModalOpen] = useState(false);
  const [pasteModalQuery, setPasteModalQuery] = useState('');

  const [buscaProdutoDebounced, setBuscaProdutoDebounced] = useState('');
  const debounce = useDebounce(500);
  const hasInitializedProducts = useRef(false);

  const descOrProdId = pasteModalQuery || buscaProdutoDebounced;

  const {
    data: productsFoundRaw,
    refetch: refetchProducts,
    isFetching,
  } = useQuery({
    queryKey: ['produtos-campanha', id, idsLojas, descOrProdId],
    queryFn: () => getProductsModalOffer(id, { storesId: idsLojas, descOrProdId }),
    initialData: [],
    retry: false,
    keepPreviousData: true,
    enabled: !!descOrProdId,
  });

  const productsFound = useMemo(() => {
    const raw = productsFoundRaw as { content: ProdutoDetalhado[] } | ProdutoDetalhado[] | undefined;
    const list = Array.isArray(raw) ? raw : raw?.content || [];
    const seen = new Set();
    return list
      .map((p: ProdutoDetalhado) => normalizeModalProductFromSearch(p))
      .filter((p: ProdutoDetalhado) => {
      const duplicate = seen.has(p.product_id);
      seen.add(p.product_id);
      return !duplicate;
      });
  }, [productsFoundRaw]);

  const productsTable = useMemo<ProdutoDetalhado[]>(() => productsTableState, [productsTableState]);
  const productsPickerData = useMemo(() => {
    const map = new Map<string, ProdutoDetalhado>();

    productsFound.forEach(item => {
      map.set(String(item.product_id), item);
    });

    productsTable.forEach(item => {
      const key = String(item.product_id);
      if (!map.has(key)) {
        map.set(key, item);
      }
    });

    return Array.from(map.values());
  }, [productsFound, productsTable]);

  const selectedProducts = useMemo(
    () => ({
      value: productsTable.map(p => p.product_id),
      cache: productsTable.map(p => ({ product_id: p.product_id, description: p.description, checked: true }) as ProdutoLista),
    }),
    [productsTable],
  );

  useEffect(() => {
    if (!show) {
      hasInitializedProducts.current = false;
      return;
    }
    if (hasInitializedProducts.current) return;
    if (!initialProducts?.length) return;

    const dedupedInitial = initialProducts.reduce<ProdutoDetalhado[]>((acc, item) => {
      if (!item?.product_id) return acc;
      if (acc.some(p => String(p.product_id) === String(item.product_id))) return acc;
      return [...acc, item];
    }, []);

    if (!dedupedInitial.length) return;

    hasInitializedProducts.current = true;
    setProductsTableState(prev => {
      const prevIds = new Set(prev.map(p => String(p.product_id)));
      const missing = dedupedInitial.filter(p => !prevIds.has(String(p.product_id)));
      return missing.length ? [...prev, ...missing] : prev;
    });
  }, [show, initialProducts]);

  const handleChangeProduct = (value: (string | number)[] | null) => {
    if (value === null) {
      setProductsTableState([]);
      refetchProducts();
    }
    if (value !== null) {
      const valueStr = value.map(String);
      const valueSet = new Set(valueStr);
      const keptProducts = productsTable.filter(item => valueSet.has(String(item.product_id)));

      const existingIdsSet = new Set(keptProducts.map(item => String(item.product_id)));
      const newIds = valueStr.filter(id => !existingIdsSet.has(id));

      let newProducts: ProdutoDetalhado[] = [];

      if (newIds.length > 0) {
        const list = productsFound;
        const productsMap = new Map(list.map(p => [String(p.product_id), p]));

        newProducts = newIds
          .map(id => {
            const found = productsMap.get(id);

            if (found) {
              return { ...found };
            }
            return null;
          })
          .filter((item): item is ProdutoDetalhado => item !== null);
      }

      const next = [...keptProducts, ...newProducts];

      setProductsTableState(next);
    }
  };

  const removeProduct = (value: ProdutoDetalhado) => {
    const productKey = value.product_uuid ? String(value.product_uuid) : String(value.product_id);
    const filtered = productsTable.filter(item => {
      const itemKey = item.product_uuid ? String(item.product_uuid) : String(item.product_id);
      return itemKey !== productKey;
    });
    setProductsTableState(filtered);
  };

  const handleSortColumn = (column: string, type: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortType(type);
  };

  const getProductsTableSorted = () => {
    if (sortColumn && sortType) {
      return [...productsTable].sort((a, b) => {
        const key = sortColumn as keyof ProdutoDetalhado;
        const valX = a[key];
        const valY = b[key];

        const x = typeof valX === 'string' ? valX.charCodeAt(0) : typeof valX === 'number' ? valX : 0;
        const y = typeof valY === 'string' ? valY.charCodeAt(0) : typeof valY === 'number' ? valY : 0;

        return sortType === 'asc' ? x - y : y - x;
      });
    }
    return productsTable;
  };

  useEffect(() => {
    debounce(() => {
      setBuscaProdutoDebounced(searchProduct);
    });
  }, [searchProduct, debounce]);

  useEffect(() => {
    setOferta({
      products: productsTable,
      ...(blocoDeOferta && {
        offer_group: blocosOfertas?.map(item => ({ name: item.label, id: item.value })).find(item => item.id === blocoDeOferta.value),
      }),
      offer: { ...offerForm, offer_type: offerTypeSelected },
    });
  }, [productsTable, blocoDeOferta, offerForm, offerTypeSelected, blocosOfertas, setOferta]);

  useEffect(() => {
    if (show) return;

    const timer = setTimeout(() => {
      setProductsTableState(prev => (prev.length > 0 ? [] : prev));

      setSearchProduct(prev => (prev !== '' ? '' : prev));
    }, 300);

    return () => clearTimeout(timer);
  }, [show]);

  const handleOpenPasteModal = () => {
    setIsPasteModalOpen(true);
  };

  const handleClosePasteModal = () => {
    setIsPasteModalOpen(false);
    setPasteModalQuery('');
  };

  const handleConfirmPasteValues = (query: string) => {
    setSearchProduct(query);
    handleClosePasteModal();
  };

  return {
    states: {
      sortColumn,
      sortType,
      productsFound: productsPickerData,
      selectedProducts,
      isPasteModalOpen,
      pasteModalQuery,
      searchProduct,
      blocoDeOferta,
      loading: isFetching,
    },
    handlers: {
      handleChangeProduct,
      removeProduct,
      handleSortColumn,
      getProductsTableSorted,
      handleOpenPasteModal,
      handleClosePasteModal,
      handleConfirmPasteValues,
      setSearchProduct,
      setPasteModalQuery,
      setBlocoDeOferta,
    },
  };
};
